<template>
  <ion-page>
    <WhiteHeader
      :back-button="true"
      :route-back="routeBack"
      title="Rapport mission"
    />

    <ion-content>
      <ion-row class="report-title">{{ month }}</ion-row>

      <DateSelector
        :days="missionDays"
        :statuses="statuses"
        :selected-day="currentDay"
        @onDateClick="onDateClick"
      />

      <div v-if="currentDay" class="mission-report-container">
        <ion-grid
          v-for="(question, key) in questions"
          :key="'question' + key"
          class="question"
        >
          <h3>{{ question.title }}</h3>

          <ion-grid v-for="field in question.fields" :key="field.property">
            <!-- title -->
            <h5 v-if="field.kind !== questionKinds.DATERANGE">
              {{ field.name }}
            </h5>

            <!-- TEXTAREA -->
            <div
              v-if="
                field.kind === questionKinds.TEXTAREA && reports[currentDay]
              "
            >
              <Textarea
                v-if="field.property"
                :title="field.name"
                :placeholders="field.placeholders"
                v-model:value="reports[currentDay][field.property]"
              />
              <Textarea
                v-else-if="reports[currentDay].custom_questions"
                :title="field.name"
                :placeholders="field.placeholders"
                v-model:value="
                  reports[currentDay].custom_questions[field.uniqid]
                "
              />
            </div>

            <!-- BOOLEAN -->
            <YesNoSelector
              v-if="field.kind === questionKinds.BOOLEAN && reports[currentDay]"
              :title="field.name"
              :value="getValueForBooleanField(field)"
              @onValueChange="onBooleanChange($event, field)"
            />

            <!-- DATE -->
            <Date
              v-if="field.kind === questionKinds.DATE"
              :title="field.name"
              :placeholders="field.placeholders"
              :selected-day="currentDay"
              v-model:date="reports[currentDay][field.property]"
            />

            <!-- DATERANGE -->
            <DateRange
              v-if="field.kind === questionKinds.DATERANGE"
              :title="field.name"
              :placeholders="field.placeholders"
              :selected-day="currentDay"
              :dates="missionDays"
              v-model:start="reports[currentDay][field.property][0]"
              v-model:end="reports[currentDay][field.property][1]"
            />

            <!-- TIMESTAMP_NUMBER -->
            <TimestampNumber
              v-if="field.kind === questionKinds.TIMESTAMP_NUMBER"
              :title="field.name"
              :placeholders="field.placeholders"
              :selected-day="currentDay"
              v-model:date="reports[currentDay][field.property + '_timestamp']"
              v-model:value="reports[currentDay][field.property]"
            />

            <!-- NUMBER -->
            <Number
              v-if="field.kind === questionKinds.NUMBER && field.uniqid"
              :title="field.name"
              :placeholders="field.placeholders"
              v-model:value="
                reports[currentDay]['custom_questions'][field.uniqid]
              "
            />
            <Number
              v-else-if="field.kind === questionKinds.NUMBER"
              :title="field.name"
              :placeholders="field.placeholders"
              v-model:value="reports[currentDay][field.property]"
            />

            <!-- DATE_NUMBER -->
            <DateNumber
              v-if="field.kind === questionKinds.DATE_NUMBER"
              :title="field.name"
              :placeholders="field.placeholders"
              v-model:date="reports[currentDay][field.property]"
            />

            <!-- FILE -->
            <!-- if uniqid it's a custom question, else it's a "static" file type (e.g. "photo") -->
            <Attachments
              v-if="field.kind === questionKinds.FILE && field.uniqid"
              v-model:attachments="
                reports[currentDay]['custom_questions'][field.uniqid]
              "
              v-model:waiting-files="waitingFiles"
              v-model:attachments-to-delete="attachmentsToDelete"
              v-model:attachments-to-update="attachmentsToUpdate"
              type-vue="create"
              :files-too-big="false"
              :kind="field.uniqid"
              :title="field.name"
            />
            <Attachments
              v-else-if="field.kind === questionKinds.FILE"
              v-model:attachments="reports[currentDay][field.property]"
              v-model:waiting-files="waitingFiles"
              v-model:attachments-to-delete="attachmentsToDelete"
              v-model:attachments-to-update="attachmentsToUpdate"
              type-vue="create"
              :files-too-big="false"
              :kind="field.property"
              :title="field.name"
            />
          </ion-grid>
        </ion-grid>
      </div>
    </ion-content>

    <div v-if="currentDay" class="bottom-buttons">
      <ion-button expand="block" fill="outline" class="save" @click="save()">
        Enregistrer le brouillon
      </ion-button>

      <ion-button expand="block" class="next-step" @click="save(true)">
        Étape suivante
      </ion-button>
    </div>
  </ion-page>
</template>

<script>
import {
  IonButton,
  IonContent,
  IonGrid,
  IonRow,
  loadingController,
} from '@ionic/vue';
import axios from 'axios';

import WhiteHeader from '@/components/driver/header/WhiteHeader';
import { getParams } from '@/services/users';
import questionKinds from '@/services/constants/questionKinds';
import utils from '@/services/utils/Utils';
import DateRange from '@/components/driver/rentalCoupon/report-components/DateRange.vue';
import Date from '@/components/driver/rentalCoupon/report-components/Date.vue';
import Textarea from '@/components/driver/rentalCoupon/report-components/Textarea.vue';
import TimestampNumber from '@/components/driver/rentalCoupon/report-components/TimestampNumber.vue';
import Number from '@/components/driver/rentalCoupon/report-components/Number.vue';
import YesNoSelector from '@/components/driver/rentalCoupon/report-components/YesNoSelector.vue';
import DateSelector from '@/components/driver/rentalCoupon/report-components/DateSelector.vue';
import Attachments from '@/components/driver/rentalCoupon/components/Attachments.vue';
import { mixinReportCommon } from '@/components/driver/rentalCoupon/mixins/ReportCommon';
import DateNumber from '@/components/driver/rentalCoupon/report-components/DateNumber.vue';

const dayjs = require('dayjs');
require('dayjs/locale/fr');
dayjs.locale('fr');

export default {
  components: {
    IonButton,
    IonContent,
    IonGrid,
    IonRow,
    WhiteHeader,
    DateSelector,

    // question components
    DateRange,
    Date,
    Textarea,
    TimestampNumber,
    Number,
    DateNumber,
    Attachments,
    YesNoSelector,
  },

  mixins: [mixinReportCommon],

  setup() {
    return {
      questionKinds,
    };
  },

  data() {
    return {
      routeBack: null,
      couponId: null,
      questions: {},
      reports: {},
      missionDays: [],
      currentDay: null,
      sending: false,

      // files
      waitingFiles: [],
      attachmentsToDelete: [],
      attachmentsToUpdate: {},
    };
  },

  mounted() {
    this.routeBack = this.$route.fullPath.split('/report')[0];
    this.couponId = this.$route.params.couponId;

    this.refreshQuestions();
  },

  methods: {
    // Always use replace to avoid going back to the report page
    goToGoodPage(createCoupon = false) {
      if (!createCoupon) {
        this.$router.replace(this.routeBack);
        return;
      }

      const currentPath = this.$route.fullPath;
      let link = '/';

      if (this.couponId) {
        const newPath = currentPath.replace('report', 'bonLocation');
        this.$router.replace(newPath);
        return;
      }

      if (currentPath.includes('chantier')) {
        link += 'chantier/' + this.$route.params.id;
        link += '/' + this.$route.params.idRide;
      } else {
        const kind = currentPath.includes('livraison')
          ? 'livraison'
          : 'transfert';

        link +=
          kind +
          '/' +
          this.$route.params.id +
          '/' +
          this.$route.params.idRide +
          '/' +
          this.$route.params.idItem;
      }

      link += '/bonLocation/create';
      this.$router.replace(link);
    },

    async refreshQuestions() {
      const dataToSend = this.getMissionRelatedData();

      try {
        const response = await axios.post(
          'driver/mission-report/list',
          dataToSend,
          await getParams(),
        );

        this.questions = response.data.questions;
        this.reports = response.data.reports;
        this.missionDays = response.data.mission_days;

        if (Object.keys(this.missionDays).length === 1) {
          this.onDateClick(Object.values(this.missionDays)[0]);
        }
      } catch (error) {
        this.sendToast(
          'Une erreur est survenue lors de la récupération des questions',
          'danger',
        );
      }
    },

    /**
     * Ensure all daterange fields are valid
     */
    validateRangeFields() {
      const flatten = Object.values(this.questions).flatMap(
        question => question.fields,
      );

      let isValid = true;
      let names = [];

      Object.values(this.reports).forEach(report => {
        flatten.forEach(field => {
          if (field.kind !== questionKinds.DATERANGE) return;

          // if end before start, break
          if (
            report[field.property][0] &&
            report[field.property][1] &&
            dayjs(report[field.property][0]).isAfter(report[field.property][1])
          ) {
            names.push(field.name);
            isValid = false;
          }
        });
      });

      if (!isValid) {
        const message =
          'La date de fin doit être après la date de début pour le(s) champ(s) concerné(s) : ';

        this.sendToast(message + '(' + names.join(', ') + ')', 'danger');
      }

      return isValid;
    },

    resetData() {
      this.routeBack = null;
      this.couponId = null;
      this.questions = {};
      this.reports = {};
      this.missionDays = [];
      this.currentDay = null;

      // files
      this.waitingFiles = [];
      this.attachmentsToDelete = [];
      this.attachmentsToUpdate = {};
    },

    async save(createCoupon = false) {
      if (this.sending) return;
      if (!this.validateRangeFields()) return;

      const result = utils.checkFilesSize(this.waitingFiles);
      if (!result.isOk) {
        utils.sendFilesErrorMessage(result.message);
        this.filesTooBig = true;

        return;
      }

      this.sending = true;
      const loading = await loadingController.create({
        message: "En cours d'envoi...",
      });
      await loading.present();

      axios
        .post(
          '/driver/mission-report',
          this.getAllDataToSend(),
          await getParams(),
        )
        .then(() => {
          this.goToGoodPage(createCoupon);
          this.sendToast('Le rapport de mission a bien été enregistré');
          this.resetData();
        })
        .catch(() => {
          this.sendToast(
            "Une erreur est survenue lors de l'enregistrement du rapport de mission",
            'danger',
          );
        })
        .finally(() => {
          this.sending = false;
          loading.dismiss();
        });
    },
  },

  computed: {
    // if we have a single day, we display the day
    month() {
      if (
        !this.currentDay ||
        !this.missionDays ||
        !Object.keys(this.missionDays).length
      ) {
        return 'Sélectionnez une date';
      }

      // get first and last day
      const days = Object.values(this.missionDays);
      const monthStart = dayjs(days[0]).format('MMMM');
      const monthEnd = dayjs(days[days.length - 1]).format('MMMM');
      const yearStart = dayjs(days[0]).format('YYYY');
      const yearEnd = dayjs(days[days.length - 1]).format('YYYY');

      if (yearStart !== yearEnd) {
        return `${monthStart} ${yearStart} - ${monthEnd} ${yearEnd}`;
      }

      if (monthStart !== monthEnd) {
        return `${monthStart} - ${monthEnd} ${yearStart}`;
      }

      return `${monthStart} ${yearStart}`;
    },

    /**
     * For each report / day, we get the completion status
     */
    statuses() {
      if (!this.reports) {
        return [];
      }

      const output = {};

      Object.entries(this.reports).forEach(([index, report]) => {
        output[index] = this.getCompletionStatuses(report);
      });

      return output;
    },
  },
};
</script>

<style scoped lang="scss">
ion-content {
  --padding-start: 0;
  --padding-end: 0;
  --padding-bottom: 0;
  --padding-top: 0;
  --background: #fffcfa;
}

.mission-report-container {
  padding: 0 16px;
  padding-bottom: 100px; /* Add padding to prevent content from being hidden behind bottom bar */
}

h3 {
  margin-top: 32px;
  font-size: 16px;
  font-weight: bold;
}

.question {
  padding: 0 8px;
}
</style>
